import { RwdSvgIcon } from '@/components/common/rwdSvgIcon/RwdSvgIcon';
import { CommonSwitch } from '@/components/common/switch';
import { AudioPlayContext } from '@/contexts/AudioPlayContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { getVHByRem } from '@/utils/commonFunc';
import { Grid } from '@mui/material';
import { useWindowSize } from '@react-hook/window-size';
import { t } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { DialogContext, SettingDialogProps } from '../..';
import { getSettingVolumeState } from '../../selector';
import { volumeSliceActions } from '../../slice/volume';
import { VolumeSlider } from './VolumeSlider';
import './index.scss';
import { TickButton } from '@/modules/popup/svg';

// const direction = 'column';
export const SoundSetting = (props: SettingDialogProps) => {
    const { setIsOpen } = props;
    const [switchMute, setSwitchMute] = useState(false);
    const [studioVolume, setStudioVolume] = useState(50);
    const [gameVolume, setGameVolume] = useState(0);

    const [preStudio, setPreStudio] = useState(studioVolume);
    const [preGame, setPreGame] = useState(gameVolume);

    const volumeState = useSelector(getSettingVolumeState);
    const { isLandscape, isApple } = useContext(ScreenContext);
    const [width, height] = useWindowSize();
    const [switchZoom, setSwitchZoom] = useState<number>(1.5);

    const { playButtonAudio } = useContext(AudioPlayContext);
    const { setExtraCloseHandler } = useContext(DialogContext);
    const dispatch = useDispatch();

    const mute = () => {
        setPreStudio(studioVolume);
        setPreGame(gameVolume);

        setStudioVolume(0);
        setGameVolume(0);
    };

    const unMute = () => {
        setStudioVolume(preStudio);
        setGameVolume(preGame);
    };

    useEffect(() => {
        setExtraCloseHandler(() => () => {});
        // set current value to the form
        setSwitchMute(volumeState.Mute);
        if (volumeState.Mute) {
            mute();
        } else {
            setStudioVolume(volumeState.Studio);
            setGameVolume(volumeState.Game);
        }
    }, []);

    useEffect(() => {
        setExtraCloseHandler(() => () => {
            dispatch(
                volumeSliceActions.updateAll({
                    Studio: studioVolume,
                    Game: gameVolume,
                    Mute: switchMute,
                    // Music: musicVolume,
                })
            );
        });
    }, [studioVolume, gameVolume, switchMute]);

    useEffect(() => {
        setSwitchMute(studioVolume === 0 && gameVolume === 0);
    }, [studioVolume, gameVolume]);
    useEffect(() => {
        const zoom = Math.max((height / 16) * 0.04, 1.5);
        setSwitchZoom(isLandscape ? zoom : 1.5);
    }, [width, height]);

    useEffect(() => {
        dispatch(
            volumeSliceActions.updateAll({
                Studio: studioVolume,
                Game: gameVolume,
                Mute: switchMute,
                // Music: musicVolume,
            })
        );
    }, [studioVolume, gameVolume, switchMute]);

    const submitHandler = () => {
        batch(() => {
            dispatch(
                volumeSliceActions.updateAll({
                    Studio: studioVolume,
                    Game: gameVolume,
                    Mute: switchMute,
                    // Music: musicVolume,
                })
            );
        }, []);
        setIsOpen(false);
        playButtonAudio();
    };
    return (
        <Grid
            container
            direction={'column'}
            justifyContent={'space-between'}
            alignItems={'center'}
            className="sound-setting"
        >
            <Grid container className="grid-container">
                <Grid
                    container
                    direction={isApple ? 'row' : 'column'}
                    justifyContent={isApple ? 'space-between' : 'unset'}
                >
                    <Grid item className="sound-setting-item-title">
                        {t('setting.game_volume')}
                    </Grid>
                    {!isApple ? (
                        <VolumeSlider
                            playButtonAudio={playButtonAudio}
                            volume={gameVolume}
                            handleVolumeChange={v => {
                                setGameVolume(v);
                                setStudioVolume(0);
                            }}
                        />
                    ) : (
                        <Grid item>
                            <CommonSwitch
                                sx={{ zoom: switchZoom }}
                                checked={gameVolume !== 0}
                                onChange={(_, checked) => {
                                    if (checked) {
                                        setSwitchMute(false);
                                        setGameVolume(100);
                                        setStudioVolume(0);
                                    } else {
                                        setGameVolume(0);
                                    }
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
                <Grid
                    container
                    direction={isApple ? 'row' : 'column'}
                    justifyContent={isApple ? 'space-between' : 'unset'}
                    className="sound-setting-item"
                >
                    <Grid item className="sound-setting-item-title">
                        {t('setting.studio_sound')}
                    </Grid>
                    {!isApple ? (
                        <Grid item>
                            <VolumeSlider
                                playButtonAudio={playButtonAudio}
                                volume={studioVolume}
                                handleVolumeChange={v => {
                                    setStudioVolume(v);
                                    setGameVolume(0);
                                }}
                            />
                        </Grid>
                    ) : (
                        <Grid item>
                            <CommonSwitch
                                sx={{ zoom: switchZoom }}
                                checked={studioVolume !== 0}
                                onChange={(_, checked) => {
                                    if (checked) {
                                        setSwitchMute(false);
                                        setStudioVolume(100);
                                        setGameVolume(0);
                                    } else {
                                        setStudioVolume(0);
                                    }
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
                <Grid
                    container
                    direction={'row'}
                    width={'100%'}
                    justifyContent={'space-between'}
                >
                    <Grid item className="sound-setting-item-title">
                        {t('setting.mute')}
                    </Grid>
                    <Grid item>
                        <CommonSwitch
                            sx={{ zoom: switchZoom }}
                            checked={switchMute}
                            onChange={(_, checked) => {
                                if (checked) {
                                    mute();
                                } else {
                                    unMute();
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {false && (
                <Grid item className="setting-confirm-button">
                    <RwdSvgIcon vSize={3} hSize={getVHByRem(3)}>
                        <TickButton onClick={submitHandler} />
                    </RwdSvgIcon>
                </Grid>
            )}
        </Grid>
    );
};
